// web/menu.ts — MAIN MENU 2.0 content: turns the persistent profile + the live
// session into the menu's section markup (Continue / New / Archives / Evolution /
// Statistics), and folds the live colony into a saveable snapshot. Pure view +
// data-shaping — no persistence side effects (profile.ts owns those), no sim impact.

import type { GameSession } from './game.ts';
import { playerVictories } from './history.ts';
import type { ColonyHistory } from './history.ts';
import { BOX_TIERS } from './evolutionBox.ts';
import { GENE_TYPE_NAME } from '../src/sim/geneData.ts';
import { REGION_DEFS } from '../src/sim/regionData.ts';
import type { PlayerProfile, ColonySnapshot } from './profile.ts';
import { lifetimeGenerations, lifetimeApexes } from './profile.ts';
import type { PowerRecords } from './colonyPower.ts';
import { effectiveSnapshot } from './colonyPower.ts';

// --- small formatting helpers ------------------------------------------------

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}
function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
function ago(ms: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// --- session → snapshot + harvests (read-only over the live world) ------------

/** Build the persistable portrait of the live colony (for profile.foldCurrent). */
export function snapshotFromSession(
  session: GameSession,
  history: ColonyHistory | null,
  name: string,
  seed: number,
  createdAt: number,
): ColonySnapshot {
  const info = session.playerInfo();
  const rank = session.playerRank();
  const row = session.leaderboard().find((r) => r.isPlayer);
  const apex = history ? playerVictories(session).length : 0;
  return {
    name: name || 'Unnamed colony',
    seed: seed >>> 0,
    createdAt,
    age: session.tick,
    generations: session.playerGeneration(),
    rank: rank.rank,
    ofColonies: rank.of,
    population: info.population,
    identity: info.identity.emerged ? info.identity.name : 'Forming',
    score: row ? row.score : 0,
    apexKills: apex,
    regionsHeld: info.regionsHeld,
    genes: info.genes,
  };
}

/** Distinct gene-TYPE ids currently in the player's inventory. */
export function geneTypesOf(session: GameSession): number[] {
  const seen: number[] = [];
  for (const g of session.playerGenes()) if (!seen.includes(g.type)) seen.push(g.type);
  return seen;
}

/** Distinct morph names the colonies have discovered — the AUTHORITATIVE read of the live
 *  discovery archive (incl. extinct branches), via `session.discoveredMorphs()`. Replaces the
 *  old chronicle harvest (which scraped 'discovery' event titles, conflating rare-gene drops
 *  with true morphs). Empty when the discovery layer isn't wired. */
export function morphsOf(session: GameSession): string[] {
  return session.discoveredMorphs();
}

// --- section markup ----------------------------------------------------------

/** The CONTINUE card body — empty string when there is no colony to continue. */
export function continueCardHTML(c: ColonySnapshot | null, live = false): string {
  if (!c) return '';
  const gen = c.generations > 0 ? `Generation ${fmt(c.generations)}` : 'Founding generation';
  // A live read (menu opened mid-run) is exact "now"; a saved read is folded at save time.
  const when = live ? 'in progress' : `saved ${ago(c.createdAt)}`;
  return (
    `<div class="mm-continue-head">` +
      `<div><span class="mm-colony-name">${esc(c.name)}</span>` +
      `<span class="muted small"> · ${gen} · ${when}</span></div>` +
    `</div>` +
    `<div class="mm-continue-grid">` +
      stat('Rank', `#${c.rank}`, `/${c.ofColonies}`) +
      stat('Colony age', fmt(c.age), 'ticks') +
      stat('Population', fmt(c.population), '') +
      stat('Specialization', esc(c.identity), '') +
      `<div class="mm-stat mm-span"><span class="mm-k">Evolution score</span>` +
      `<span class="mm-v mm-accent">${fmt(c.score)}</span></div>` +
    `</div>`
  );
}

/** The NEW-COLONY starting-region preview (always the Backyard — the safe ground). */
export function newColonyPreviewHTML(seed: number): string {
  const r = REGION_DEFS[0];
  const name = r ? r.name : 'Backyard';
  const glyph = r ? r.glyph : 'B';
  const desc = r ? r.description : 'The colony\'s first ground.';
  const foes = r ? r.enemies.slice(0, 3).map((e) => esc(e.name)).join(' · ') : '';
  return (
    `<div class="mm-region-preview">` +
      `<div class="mm-region-glyph">${esc(glyph)}</div>` +
      `<div class="mm-region-body">` +
        `<div class="mm-region-name">Starting region — ${esc(name)}</div>` +
        `<div class="mm-region-desc">${esc(desc)}</div>` +
        (foes ? `<div class="mm-region-meta">Native threats: ${foes}</div>` : '') +
        `<div class="mm-region-meta">World seed <b>${seed >>> 0}</b></div>` +
      `</div>` +
    `</div>`
  );
}

/** COLONY ARCHIVES — the roll of honour + lifetime bests. */
export function archivesHTML(profile: PlayerProfile): string {
  const best = profile.bestRank > 0 ? `#${profile.bestRank}` : '—';
  const summary =
    `<div class="mm-stats-grid">` +
      bigStat('Highest placement', best) +
      bigStat('Apex kills', fmt(lifetimeApexes(profile))) +
      bigStat('Regions conquered', fmt(profile.mostRegions)) +
      bigStat('Notable discoveries', fmt(profile.morphsSeen.length)) +
    `</div>`;
  const discoveries = profile.morphsSeen.length
    ? `<h4>Notable discoveries</h4><div class="mm-chips">` +
      profile.morphsSeen.slice(-14).map((m) => `<span class="mm-chip on">${esc(m)}</span>`).join('') +
      `</div>`
    : '';
  if (profile.archives.length === 0) {
    return summary +
      `<p class="muted small">No retired colonies yet. Grow a colony, then start a new one — its lineage will be recorded here.</p>` +
      discoveries;
  }
  const rows = profile.archives.map((a) =>
    `<li class="mm-archive-row">` +
      `<div class="mm-archive-head"><b>${esc(a.name)}</b> ` +
      `<span class="muted small">seed ${a.seed} · ${esc(a.cause)} · ${ago(a.retiredAt)}</span></div>` +
      `<div class="mm-archive-stats">` +
        `<span>Rank #${a.rank}/${a.ofColonies}</span>` +
        `<span>${esc(a.identity)}</span>` +
        `<span>Pop ${fmt(a.population)}</span>` +
        `<span>Gen ${fmt(a.generations)}</span>` +
        `<span>${fmt(a.apexKills)} apex</span>` +
        `<span>${fmt(a.regionsHeld)} regions</span>` +
        `<span class="mm-accent">${fmt(a.score)} score</span>` +
      `</div>` +
    `</li>`).join('');
  return summary + `<h4>Roll of honour</h4><ul class="mm-archive-list">${rows}</ul>` + discoveries;
}

/** EVOLUTION ARCHIVE — discovered genes / morphs / box tiers / mutation power. */
export function evolutionHTML(profile: PlayerProfile, session: GameSession | null): string {
  const geneSet = new Set<number>(profile.geneTypesSeen);
  if (session) for (const t of geneTypesOf(session)) geneSet.add(t);
  const tierSet = new Set<number>(profile.tiersOpened);
  const geneChips = GENE_TYPE_NAME.map((name, i) =>
    `<span class="mm-chip ${geneSet.has(i) ? 'on' : 'off'}">${esc(name)}</span>`).join('');
  const tierChips = BOX_TIERS.map((name, i) =>
    `<span class="mm-tier ${tierSet.has(i) ? 'on' : 'off'}">${esc(name)}</span>`).join('');
  const morphChips = profile.morphsSeen.length
    ? profile.morphsSeen.slice(-24).map((m) => `<span class="mm-chip on">${esc(m)}</span>`).join('')
    : `<span class="muted small">None yet — splice synergistic genes to unlock emergent morphs.</span>`;
  return (
    `<div class="mm-stats-grid">` +
      bigStat('Genes discovered', `${geneSet.size}<small>/${GENE_TYPE_NAME.length}</small>`) +
      bigStat('Morphs discovered', fmt(profile.morphsSeen.length)) +
      bigStat('Box tiers unlocked', `${tierSet.size}<small>/${BOX_TIERS.length}</small>`) +
      bigStat('Total mutation power', fmt(profile.peakEnergy)) +
    `</div>` +
    `<h4>Gene library</h4><div class="mm-chips">${geneChips}</div>` +
    `<h4>Evolution box tiers</h4><div class="mm-chips">${tierChips}</div>` +
    `<h4>Morphs &amp; discoveries</h4><div class="mm-chips">${morphChips}</div>`
  );
}

/** STATISTICS — the lifetime tallies. */
export function statisticsHTML(profile: PlayerProfile, records: PowerRecords | null = null): string {
  const cells: Array<[string, number]> = [
    ['Colonies created', profile.coloniesCreated],
    ['Generations bred', lifetimeGenerations(profile)],
    ['Apexes defeated', lifetimeApexes(profile)],
    ['Evolution boxes opened', profile.boxesOpened],
    ['Genes sacrificed', profile.genesSacrificed],
    ['Highest gene score', profile.peakScore],
  ];
  // All-time COLONY POWER bests (web/colonyPower.ts records; device-local, never reset by a
  // New Colony). Passed in so this builder stays pure/testable; absent ⇒ section omitted.
  // Records are stored RAW; shown through the same DIMINISHING-returns curve as the HUD, so
  // Best Colony reads as the SUM of the effective caste-bests (parts == whole).
  if (records) {
    const eff = effectiveSnapshot(records);
    cells.push(['Best Colony Power', eff.colony]);
    cells.push(['Best Worker Power', eff.worker]);
    cells.push(['Best Soldier Power', eff.soldier]);
    cells.push(['Best Flyer Power', eff.flyer]);
    cells.push(['Best Specialist Power', eff.specialist]);
  }
  return `<div class="mm-stats-grid wide">` +
    cells.map(([k, v]) => bigStat(k, fmt(v))).join('') +
    `</div>`;
}

function stat(k: string, v: string, unit: string): string {
  return `<div class="mm-stat"><span class="mm-k">${k}</span>` +
    `<span class="mm-v">${v}${unit ? `<small>${unit}</small>` : ''}</span></div>`;
}
function bigStat(k: string, v: string): string {
  return `<div class="mm-stat big"><span class="mm-v mm-accent">${v}</span><span class="mm-k">${k}</span></div>`;
}
